/**
 * /admin/ad-campaigns — Paid campaign manager for the Social Pulse
 * Ad Network. Sits alongside /admin/ad-network and takes precedence
 * over house ads at /api/ads/serve time.
 *
 * Flow:
 *   1. List all campaigns (any status) with "serving now" badge + today's cap-progress bar.
 *   2. Create a new campaign (name, creative, target sisters, placements, dates, cap).
 *   3. Edit a campaign inline. Pause / resume / delete.
 *
 * Pricing/budget is NOT wired here yet — this is the operations
 * surface. Billing comes in a later sprint.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Megaphone, Plus, Pause, Play, Trash2,
  Calendar, Target, Image as ImageIcon, Save, X, RefreshCw,
  CheckCircle2, CircleAlert,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const PLACEMENTS = [
  { code: "A1", label: "Top Leaderboard" },
  { code: "A2", label: "Medium Rectangle" },
  { code: "N1", label: "Native Inline" },
  { code: "H1", label: "Hero Sponsor" },
];

const EMPTY_FORM = {
  id: null,
  name: "",
  status: "active",
  creative: { headline: "", body: "", cta_label: "Learn more", click_url: "", hero_url: "" },
  sites: [],
  placements: ["A1", "A2"],
  start_at: new Date().toISOString().slice(0, 10),
  end_at: new Date(Date.now() + 7 * 86400 * 1000).toISOString().slice(0, 10),
  daily_cap: null,
  priority: 100,
};


export default function AdminAdCampaigns() {
  const { user, isAdmin } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [sisters, setSisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, grid] = await Promise.all([
        http.get("/ads/admin/campaigns"),
        http.get("/ads/admin/grid"),
      ]);
      setCampaigns(cs.data.campaigns || []);
      // Derive the sister list from the grid (which already filters out "socialpulse").
      const bySlug = new Map();
      for (const c of grid.data.cells || []) {
        if (!bySlug.has(c.portal)) bySlug.set(c.portal, { slug: c.portal, name: c.portal_name, domain: c.domain });
      }
      setSisters([...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin, load]);

  if (!user) return <GuardShell><Link to="/login" className="underline">Sign in to continue</Link></GuardShell>;
  if (!isAdmin) return <GuardShell>Admin access required.</GuardShell>;

  const startEdit = (c) => {
    setForm({
      id: c.id, name: c.name, status: c.status,
      creative: { ...c.creative },
      sites: [...(c.sites || [])],
      placements: [...(c.placements || [])],
      start_at: (c.start_at || "").slice(0, 10),
      end_at:   (c.end_at   || "").slice(0, 10),
      daily_cap: c.daily_cap || null,
      priority: c.priority || 100,
    });
    setShowForm(true);
  };

  const togglePause = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    try {
      await http.patch(`/ads/admin/campaigns/${c.id}`, { status: next });
      toast.success(`${c.name} ${next === "active" ? "resumed" : "paused"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This can't be undone.`)) return;
    try {
      await http.delete(`/ads/admin/campaigns/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.creative.headline || !form.creative.click_url) {
      toast.error("Name, headline and click URL are required.");
      return;
    }
    if (!form.sites.length) { toast.error("Pick at least one sister site."); return; }
    if (!form.placements.length) { toast.error("Pick at least one placement."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        status: form.status,
        creative: { ...form.creative, hero_url: form.creative.hero_url || null },
        sites: form.sites,
        placements: form.placements,
        start_at: new Date(form.start_at).toISOString(),
        end_at:   new Date(form.end_at + "T23:59:59Z").toISOString(),
        daily_cap: form.daily_cap || null,
        priority: Number(form.priority) || 100,
      };
      if (form.id) {
        await http.patch(`/ads/admin/campaigns/${form.id}`, payload);
      } else {
        await http.post("/ads/admin/campaigns", payload);
      }
      toast.success(form.id ? "Campaign updated" : "Campaign created");
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-ad-campaigns-page">
      <SocialPulseHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/admin/ad-network" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
              <ArrowLeft className="w-3 h-3" /> Back to Ad Network
            </Link>
            <h1 className="font-serif text-3xl font-black text-slate-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-blue-600" /> Paid Campaigns
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Paid campaigns serve before house cross-promos at <code className="font-mono">/api/ads/serve</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
              className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold px-4 py-2"
              data-testid="admin-campaigns-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button onClick={() => { setForm(EMPTY_FORM); setShowForm(true); }}
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2"
              data-testid="admin-campaigns-new">
              <Plus className="w-3.5 h-3.5" /> New campaign
            </button>
          </div>
        </div>

        {loading && !campaigns.length && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && !campaigns.length && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="admin-campaigns-empty">
            <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="font-bold text-slate-700">No campaigns yet</div>
            <div className="text-sm text-slate-500 mt-1">Create your first paid campaign — it will serve before house ads on the sisters you pick.</div>
          </div>
        )}

        <div className="space-y-3" data-testid="admin-campaigns-list">
          {campaigns.map((c) => (
            <CampaignRow key={c.id} c={c} sisters={sisters}
              onEdit={() => startEdit(c)} onPause={() => togglePause(c)} onDelete={() => remove(c)} />
          ))}
        </div>

        {showForm && (
          <CampaignForm
            form={form} setForm={setForm} sisters={sisters}
            saving={saving} onSubmit={submit} onClose={() => setShowForm(false)}
          />
        )}
      </main>
      <SocialPulseFooter />
    </div>
  );
}


function CampaignRow({ c, sisters, onEdit, onPause, onDelete }) {
  const now = new Date().toISOString();
  const live = c.serving_now;
  const sisterNames = (c.sites || []).map((s) =>
    (sisters.find((x) => x.slug === s) || {}).name || s
  );
  const capProgress = c.daily_cap ? Math.min(100, Math.round((c.served_today / c.daily_cap) * 100)) : null;
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex gap-4 items-start" data-testid={`admin-campaign-${c.id}`}>
      <div className="w-24 shrink-0">
        {c.creative?.hero_url ? (
          <img src={c.creative.hero_url} alt="" className="w-24 h-24 object-cover rounded-lg bg-slate-100" />
        ) : (
          <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <div className="font-bold text-slate-900 truncate">{c.name}</div>
          <StatusBadge status={c.status} live={live} />
          <span className="text-[11px] text-slate-500">priority {c.priority}</span>
        </div>
        <div className="text-sm text-slate-700 line-clamp-1">{c.creative?.headline}</div>
        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> {sisterNames.length} sister{sisterNames.length !== 1 ? "s" : ""} · {c.placements?.join(", ")}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {(c.start_at||"").slice(0,10)} → {(c.end_at||"").slice(0,10)}</span>
          {c.daily_cap && (
            <span>cap {c.daily_cap.toLocaleString()}/day</span>
          )}
        </div>
        {c.daily_cap && (
          <div className="mt-2">
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${capProgress >= 100 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${capProgress}%` }} />
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {c.served_today?.toLocaleString() || 0} / {c.daily_cap.toLocaleString()} today
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Link to={`/admin/ad-campaigns/${c.id}`}
          className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
          data-testid={`admin-campaign-${c.id}-analytics`}>Analytics</Link>
        <button onClick={onPause}
          className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-600"
          title={c.status === "active" ? "Pause" : "Resume"}
          data-testid={`admin-campaign-${c.id}-pause`}>
          {c.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={onEdit}
          className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
          data-testid={`admin-campaign-${c.id}-edit`}>Edit</button>
        <button onClick={onDelete}
          className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center text-red-500"
          title="Delete"
          data-testid={`admin-campaign-${c.id}-delete`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function StatusBadge({ status, live }) {
  if (live) return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> LIVE</span>;
  if (status === "paused") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">PAUSED</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"><CircleAlert className="w-3 h-3" /> SCHEDULED</span>;
}


function CampaignForm({ form, setForm, sisters, saving, onSubmit, onClose }) {
  const toggleSite = (slug) => {
    setForm((f) => ({ ...f, sites: f.sites.includes(slug) ? f.sites.filter((s) => s !== slug) : [...f.sites, slug] }));
  };
  const togglePlacement = (code) => {
    setForm((f) => ({ ...f, placements: f.placements.includes(code) ? f.placements.filter((p) => p !== code) : [...f.placements, code] }));
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center overflow-y-auto p-4" data-testid="admin-campaign-form">
      <form onSubmit={onSubmit} className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="font-serif text-xl font-bold">{form.id ? "Edit campaign" : "New campaign"}</div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <Field label="Internal name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Q4 Travel Push — Jodhpur special"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="admin-campaign-name" required />
          </Field>

          <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
            <legend className="text-[10px] uppercase font-bold tracking-widest text-slate-500 px-1">Creative</legend>
            <Field label="Headline">
              <input value={form.creative.headline}
                onChange={(e) => setForm({ ...form, creative: { ...form.creative, headline: e.target.value } })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="admin-campaign-headline" required />
            </Field>
            <Field label="Body (short)">
              <input value={form.creative.body}
                onChange={(e) => setForm({ ...form, creative: { ...form.creative, body: e.target.value } })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label">
                <input value={form.creative.cta_label}
                  onChange={(e) => setForm({ ...form, creative: { ...form.creative, cta_label: e.target.value } })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Click URL">
                <input value={form.creative.click_url} type="url"
                  onChange={(e) => setForm({ ...form, creative: { ...form.creative, click_url: e.target.value } })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="admin-campaign-clickurl" required />
              </Field>
            </div>
            <Field label="Hero image URL (optional)">
              <input value={form.creative.hero_url || ""} type="url"
                onChange={(e) => setForm({ ...form, creative: { ...form.creative, hero_url: e.target.value } })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                placeholder="https://…" />
            </Field>
          </fieldset>

          <Field label={`Target sisters (${form.sites.length})`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto p-3 bg-slate-50 rounded-lg">
              {sisters.map((s) => (
                <label key={s.slug} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.sites.includes(s.slug)}
                    onChange={() => toggleSite(s.slug)}
                    data-testid={`admin-campaign-site-${s.slug}`} />
                  <span className="truncate">{s.name}</span>
                </label>
              ))}
            </div>
          </Field>

          <Field label={`Placements (${form.placements.length})`}>
            <div className="flex flex-wrap gap-2">
              {PLACEMENTS.map((p) => {
                const on = form.placements.includes(p.code);
                return (
                  <button type="button" key={p.code} onClick={() => togglePlacement(p.code)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}
                    data-testid={`admin-campaign-placement-${p.code}`}>
                    {p.code} · {p.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input type="date" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="admin-campaign-start" required />
            </Field>
            <Field label="End date">
              <input type="date" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="admin-campaign-end" required />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily impression cap (optional)">
              <input type="number" min="0" value={form.daily_cap || ""}
                onChange={(e) => setForm({ ...form, daily_cap: e.target.value ? Number(e.target.value) : null })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                placeholder="unlimited"
                data-testid="admin-campaign-cap" />
            </Field>
            <Field label="Priority (higher wins)">
              <input type="number" min="1" max="10000" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="admin-campaign-priority" />
            </Field>
          </div>

          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="admin-campaign-status">
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose}
            className="text-sm text-slate-600 hover:text-slate-900 font-semibold px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2 rounded-full"
            data-testid="admin-campaign-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {form.id ? "Save changes" : "Create campaign"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}


function GuardShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      {children}
    </div>
  );
}
